import { useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, confirmEmailVerification, getAuthMode } from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { Button } from "@/components/ui/Button";
import { exchangeKratosSession } from "@/features/auth/kratos/exchange";
import { KratosVerificationForm } from "@/features/auth/kratos/KratosVerificationForm";

/**
 * `/verify-email` landing.
 *
 * Kratos mode: drives the Kratos verification flow (code strategy).
 * Email deep-links arrive as `?flow=<id>&code=<code>` and auto-submit;
 * without params the form asks which address to send a code to. After
 * verification the page tries the session exchange — same-browser
 * registrations land signed in, foreign browsers get a sign-in link.
 *
 * Legacy mode: the original `?token=` form against
 * `/api/me/email-verification/confirm` (token IS the auth).
 *
 * Privacy invariant: tokens/codes are read once from the search params
 * into normal inputs; never logged, cleared after submit.
 */
export function VerifyEmailPage() {
  const search = useSearch({ from: "/verify-email" });
  const authMode = useQuery({
    queryKey: unscopedKey("auth-mode"),
    queryFn: getAuthMode,
    staleTime: 60_000,
  });
  const kratosEnabled = authMode.data?.kratos_enabled ?? false;

  return (
    <main
      data-testid="verify-email-page"
      className="min-h-screen bg-canvas flex items-center justify-center p-4"
    >
      <section
        aria-label="Verify your email"
        className="w-full max-w-md rounded border border-rule bg-surface p-6 flex flex-col gap-3"
      >
        <header>
          <h1 className="text-xl font-display tracking-tight text-ink">
            Verify email
          </h1>
          <p className="text-ink-muted text-xs">
            {kratosEnabled
              ? "Enter the one-time code from your email to confirm ownership."
              : "Paste the verification token from your email to confirm ownership."}
          </p>
        </header>

        {authMode.isPending ? (
          <p className="font-mono text-xs text-ink-muted">
            Checking sign-in options...
          </p>
        ) : kratosEnabled ? (
          <KratosVerifyBody flowId={search.flow} code={search.code} />
        ) : (
          <LegacyTokenBody initialToken={search.token ?? ""} />
        )}
      </section>
    </main>
  );
}

function KratosVerifyBody({ flowId, code }: { flowId?: string; code?: string }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [verifiedWithoutSession, setVerifiedWithoutSession] = useState(false);

  const handleVerified = async () => {
    try {
      // Same-browser flows still hold the Kratos session from
      // registration — verification just unlocked the exchange.
      await exchangeKratosSession();
      await queryClient.invalidateQueries();
      await queryClient.invalidateQueries({ queryKey: unscopedKey("me") });
      void navigate({ to: "/hub", replace: true });
    } catch {
      // No live Kratos session here (e.g. link opened on another
      // device) — verified, but they still need to sign in.
      setVerifiedWithoutSession(true);
    }
  };

  if (verifiedWithoutSession) {
    return (
      <div className="flex flex-col gap-2">
        <p data-testid="verify-email-success" className="text-good text-sm font-mono">
          Email verified. Sign in to continue.
        </p>
        <Link
          to="/login"
          data-testid="verify-email-login"
          className="text-xs text-accent hover:underline self-start"
        >
          Go to sign-in
        </Link>
      </div>
    );
  }

  return (
    <KratosVerificationForm
      flowId={flowId}
      initialCode={code}
      onVerified={handleVerified}
      testIdPrefix="verify-email-kratos"
    />
  );
}

function LegacyTokenBody({ initialToken }: { initialToken: string }) {
  const [token, setToken] = useState(initialToken);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: (t: string) => confirmEmailVerification({ token: t.trim() }),
    onSuccess: () => {
      setSuccess(true);
      setToken("");
    },
    onError: (err) => {
      if (err instanceof ApiError && err.status === 400) {
        setError(
          typeof err.detail === "string"
            ? err.detail
            : "That verification token is invalid or expired.",
        );
      } else {
        setError(formatErr(err));
      }
    },
  });

  if (success) {
    return (
      <div className="flex flex-col gap-2">
        <p data-testid="verify-email-success" className="text-good text-sm font-mono">
          Email verified. Thank you for confirming.
        </p>
        <Link
          to="/register"
          data-testid="verify-email-open-me"
          className="text-xs text-accent hover:underline self-start"
        >
          Continue to team access
        </Link>
      </div>
    );
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setError(null);
        if (token.trim().length < 16) {
          setError("Verification token must be at least 16 characters.");
          return;
        }
        mut.mutate(token);
      }}
      className="flex flex-col gap-2"
    >
      <label className="flex flex-col gap-1 text-xs">
        <span className="font-display uppercase tracking-wider text-ink-muted">
          Verification token
        </span>
        <input
          type="text"
          value={token}
          onChange={(e) => setToken(e.target.value)}
          autoFocus
          data-testid="verify-email-token"
          className="rounded border border-rule bg-canvas px-2 py-1.5 text-xs font-mono text-ink focus:outline-none focus:ring-2 focus:ring-accent"
        />
      </label>

      {error ? (
        <p data-testid="verify-email-error" className="text-destructive text-xs font-mono">
          {error}
        </p>
      ) : null}

      <Button
        type="submit"
        size="sm"
        disabled={mut.isPending}
        data-testid="verify-email-submit"
        className="self-start"
      >
        {mut.isPending ? "Verifying…" : "Verify email"}
      </Button>
    </form>
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Network error. Try again.";
}
